<template>
  <div class="main">
    <div id="map" ref="map" class="map"></div>

    <!-- Zoom buttons  -->
    <div class="zoom-buttons">
      <a
        :class="[zoomButtonsColorClass]"
        class="button push-left-xs"
        @click="!zoomInMapDisabled && zoomMap(1)"
        :disabled="zoomInMapDisabled"
      >
        <i class="icon-zoom-in"></i>
      </a>

      <a
        :class="[zoomButtonsColorClass]"
        class="button push-left-xs"
        @click="!zoomOutMapDisabled && zoomMap(-1)"
        :disabled="zoomOutMapDisabled"
      >
        <i class="icon-zoom-out"></i>
      </a>
    </div>

    <!-- Date picker -->
    <div class="panel is-marginless date-picker">
      <div class="panel-content overflow-auto">
        <h6 class="title is-6 push-bottom-xs">Pick date</h6>

        <Spinner v-if="isLoadingHistoryDates" />

        <div class="notification is-danger" v-if="historyDatesLoadError">
          {{ historyDatesLoadError }}
        </div>

        <FlatPickr
          v-if="historyDates && !hasNoHistoryDates"
          v-model="selectedDate"
          :config="dateConfig"
          class="input"
        />

        <p v-if="hasNoHistoryDates" class="push-top-xxs is-marginless-bottom">
          <i class="icon-alert-circle"></i> No positions
        </p>
      </div>
    </div>

    <!-- Settings -->
    <div class="panel is-marginless heatmap">
      <div class="panel-content overflow-auto">
        <div class="field has-addons">
          <p class="control">
            <button
              class="button is-small"
              :class="{ 'is-primary': selectedMapTypeId === 'roadmap' }"
              @click="selectMapType('roadmap')"
            >
              <span>Standard</span>
            </button>
          </p>
          <p class="control">
            <button
              class="button is-small"
              :class="{ 'is-primary': selectedMapTypeId === 'satellite' }"
              @click="selectMapType('satellite')"
            >
              <span>Satellit</span>
            </button>
          </p>
        </div>

        <div class="field is-horizontal">
          <div class="field-label">
            <label class="label has-text-weight-normal">GSM Coverage</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <label class="checkbox">
                  <input
                    v-if="!isLoadingHeatmap"
                    class="is-danger"
                    type="checkbox"
                    v-model="displayHeatmap"
                  />
                  <Spinner v-if="isLoadingHeatmap" width="15px" height="15px" />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="notification is-danger" v-if="heatmapLoadError">{{ heatmapLoadError }}</div>
      </div>
    </div>

    <!-- Slider -->
    <div class="gps-controls box" v-if="sliderVisible">
      <Spinner v-if="isLoadingGPSPositions" />

      <div class="notification is-danger" v-if="gpsPositionsLoadError">
        {{ gpsPositionsLoadError }}
      </div>

      <div class="drag-elapsed" v-if="gpsPositions">
        <RangeSlider v-model="elapsed" :max="gpsPositions.length - 1" :step="1" value="0" min="0">
          <template v-slot:timeline>
            <div
              v-for="(segment, i) in segments"
              :key="i"
              :style="[getSliderClass(segment)]"
              class="drag-elapsed-segment"
            ></div>
          </template>
        </RangeSlider>

        <div class="drag-elapsed-timer">{{ currentTimeStamp }} / {{ maxTimeStamp }}</div>
      </div>
    </div>

    <!-- Info panel -->
    <div class="info-panel-container">
      <div class="panel is-marginless info-panel" v-if="sliderVisible">
        <div class="panel-content overflow-auto">
          <div class="media">
            <div class="media-content">
              <h6 class="title is-6 push-bottom-xs">{{ currentTimeStamp }}</h6>

              <ul>
                <li>Coverage <IconBars :bars="currentPositionCoverage" /></li>

                <li>GPS: {{ currentPositionGps }}%</li>

                <li>GSM: {{ currentPositionCsq }}</li>

                <li>Battery: {{ currentPositionBattery }}%</li>

                <li>Latency: {{ currentPositionLatency }}s</li>

                <li>Latitude: {{ currentPositionLatitude }}</li>

                <li>Longitude: {{ currentPositionLongitude }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import api from '@/api/api.js'
import Spinner from '@/components/utils/Spinner'
import RangeSlider from './RangeSlider'
import IconBars from './IconBars'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

import mapStyles from './mapStyles'

const MAP_MAX_ZOOM = 16
const MAP_MIN_ZOOM = 5

const coverageToMapSegmentColor = {
  0: '#00000000', // Transparent, segments of "dead" positions should not be visible
  1: '#e74c3c', // Red
  3: '#f1c40f', // Yellow
  4: '#2ecc71' // Green
}

const coverageToSliderSegmentColor = {
  0: '#222222', // Black
  1: '#e74c3c', // Red
  3: '#f1c40f', // Yellow
  4: '#ecf0f1' // Grey
}

import {
  LOAD_HISTORY_DATES,
  IS_LOADING_HISTORY_DATES,
  GET_HISTORY_DATES,
  GET_HISTORY_DATES_LOAD_ERROR,
  HAS_EMPTY_HISTORY_DATES_LOAD_RESULT,
  LOAD_GPS_POSITIONS_FOR_DATE,
  IS_LOADING_GPS_POSITIONS,
  GET_GPS_POSITIONS,
  GET_GPS_POSITIONS_LOAD_ERROR,
  HAS_EMPTY_GPS_POSITIONS_LOAD_RESULT
} from './weHuntGPSStore'

export default {
  name: 'GPSCoverageMap',

  components: { Spinner, FlatPickr, RangeSlider, IconBars },

  props: {
    gpsStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      map: null,
      selectedMapTypeId: 'roadmap',
      currentZoom: 5,
      selectedDate: null,
      elapsed: 0,
      segments: [],
      currentPositionMarker: null,
      polylines: [],
      displayHeatmap: true,
      heatmapUpdateTimer: null,
      heatmapCenter: null,
      heatmapData: null,
      isLoadingHeatmap: false,
      heatmapLoadError: null
    }
  },

  mounted() {
    this.initializeMap()
    this.loadGPSData()

    this.updateSelectedDate(this.historyDates)
    this.updateSegments(this.gpsPositions)
    this.updateGPSMarker(this.currentPosition)
  },

  methods: {
    initializeMap() {
      const mapOptions = {
        zoom: this.currentZoom,
        maxZoom: MAP_MAX_ZOOM,
        disableDefaultUI: true,
        fullscreenControl: false,
        scaleControl: true,
        mapTypeControl: false,
        center: {
          lat: 59.7,
          lng: 14.5
        },
        scrollwheel: true,
        styles: mapStyles
      }

      this.map = new window.google.maps.Map(this.$refs['map'], mapOptions)

      this.map.addListener('zoom_changed', () => {
        this.currentZoom = this.map.getZoom()
      })

      this.map.addListener('center_changed', () => {
        this.setHeatmapUpdateNeeded()
      })

      this.map.addListener('bounds_changed', () => {
        this.setHeatmapUpdateNeeded()
      })

      this.currentPositionMarker = new window.google.maps.Marker({
        map: this.map,
        clickable: false,
        visible: false,
        icon: {
          url: '/img/markers/marker-dog.svg',
          anchor: new window.google.maps.Point(14, 14)
        }
      })

      const heatmapGradient = ['rgba(235, 75, 75, 0)', 'rgba(200, 40, 40, 1)']
      this.heatmap = new window.google.maps.visualization.HeatmapLayer({
        map: this.map,
        radius: 0.01,
        maxIntensity: 50,
        dissipating: false,
        gradient: heatmapGradient
      })
    },

    updateSelectedDate(dates) {
      if (!dates || dates.length === 0) {
        this.selectedDate = null
        return
      }

      this.selectedDate = dayjs(dates[dates.length - 1]).toDate()
    },

    updateSegments(positions) {
      this.segments = []
      this.elapsed = 0

      if (positions) {
        const segments = this.createPositionSegments(positions, 0)
        this.segments = segments
        this.updateMapBounds()
        this.updatePolylines(segments)
      }
    },

    updateGPSMarker(position) {
      if (position === null) {
        this.currentPositionMarker.visible = false
        return
      }

      const coverage = this.calculateCoverage(position)
      if (coverage == 0) {
        // GPS marker should not move to "dead" positions
        return
      }

      this.currentPositionMarker.setPosition(position.location)
      this.currentPositionMarker.visible = true
    },

    loadGPSData() {
      const loadHistoryDatesAction = `${this.gpsStorePath}/${LOAD_HISTORY_DATES}`
      this.$store.dispatch(loadHistoryDatesAction)
    },

    zoomMap(step) {
      this.currentZoom += step
      this.map.setZoom(this.currentZoom)
    },

    getSliderClass(segment) {
      const segmentCoverage = segment.coverage
      let color = coverageToSliderSegmentColor[segmentCoverage]

      return {
        width: (segment.positions.length * 100) / this.gpsPositions.length + '%',
        background: color
      }
    },

    createPositionSegments(allPositions, segmentStartPositionIndex) {
      const segmentFirstPosition = allPositions[segmentStartPositionIndex]
      const segmentCoverage = this.calculateCoverage(segmentFirstPosition)

      const segment = {
        coverage: segmentCoverage,
        positions: []
      }

      // If the position just before the start of this segment has better coverage than this
      // segment add a line to the end of the previous segment. This means that lines between
      // positions with different coverage always displayes the lower coverage.
      if (segmentStartPositionIndex > 0) {
        const previousPosition = allPositions[segmentStartPositionIndex - 1]
        const previousPositionCoverage = this.calculateCoverage(previousPosition)
        if (previousPositionCoverage > segmentCoverage) {
          segment.positions.push(allPositions[segmentStartPositionIndex - 1])
        }
      }

      for (let i = segmentStartPositionIndex; i < allPositions.length; i++) {
        const position = allPositions[i]
        const positionCoverage = this.calculateCoverage(position)

        if (positionCoverage == segmentCoverage) {
          // Continue to add positions to this segment as long as the coverage does not change.
          segment.positions.push(position)
        } else {
          // Add first position of the next segment to the end of this segment following the same logic
          // as for adding a line to the previous segment above.
          if (positionCoverage > segmentCoverage) {
            segment.positions.push(position)
          }

          return [segment, ...this.createPositionSegments(allPositions, i)]
        }
      }

      return [segment]
    },

    calculateCoverage(position) {
      if (!position) {
        return null
      }

      let csq = position.csq
      let gps = this.calculateGPS(position)
      let latency = this.calculateLatency(position)

      if (!csq && !gps && !latency) {
        return null
      }

      if (
        (gps !== null && gps == 0) ||
        (csq !== null && csq == 0) ||
        (latency !== null && latency > 120)
      ) {
        return 0
      }

      if (
        (gps !== null && gps <= 60) ||
        (csq !== null && csq < 4) ||
        (latency !== null && latency >= 15)
      ) {
        return 1
      }

      if (
        (gps !== null && gps <= 80) ||
        (csq !== null && csq < 8) ||
        (latency !== null && latency >= 5)
      ) {
        return 3
      }

      return 4
    },

    calculateGPS(position) {
      if (!position) {
        return null
      }

      const signals = {
        1: 100,
        11: 80,
        21: 60,
        31: 40,
        41: 20,
        51: 0
      }

      const signal = position.gpsSignal
      return signals[signal]
    },

    calculateLatency(position) {
      if (!position) {
        return null
      }

      const a = new Date(position.timestamp)
      const b = new Date(position.receivedAt)

      const difference = (b - a) / 1000

      return difference
    },

    updateMapBounds() {
      if (!this.gpsPositions || this.gpsPositions.length < 2) {
        return
      }

      const bounds = new window.google.maps.LatLngBounds()

      this.gpsPositions.forEach(position => {
        const { lat, lng } = position.location
        bounds.extend(new window.google.maps.LatLng(lat, lng))
      })

      this.map.fitBounds(bounds)
    },

    updatePolylines(segments) {
      this.polylines.forEach(polyline => {
        polyline.setMap(null)
      })
      this.polylines = []

      if (!segments) {
        return
      }

      segments.forEach(segment => {
        this.polylines.push(this.createPolyline(segment))
      })
    },

    createPolyline(segment) {
      const coordinates = segment.positions.map(position => {
        return new window.google.maps.LatLng(position.location)
      })

      const segmentCoverage = segment.coverage
      let color = coverageToMapSegmentColor[segmentCoverage]

      const polyline = new window.google.maps.Polyline({
        map: this.map,
        clickable: false,
        path: coordinates,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: 1.0,
        strokeWeight: 4
      })

      return polyline
    },

    setHeatmapUpdateNeeded() {
      if (!this.displayHeatmap) {
        return
      }

      clearTimeout(this.heatmapUpdateTimer)
      this.heatmapUpdateTimer = setTimeout(this.updateHeatmap, 500)
    },

    async updateHeatmap() {
      const mapCenter = this.map.getCenter()

      if (!mapCenter) {
        return
      }

      const heatmapCenter = {
        lat: mapCenter.lat().toFixed(1),
        lng: mapCenter.lng().toFixed(1)
      }

      if (
        this.heatmapCenter != null &&
        this.heatmapCenter.lat === heatmapCenter.lat &&
        this.heatmapCenter.lng === heatmapCenter.lng
      ) {
        return
      }

      this.heatmapCenter = heatmapCenter

      this.heatmapLoadError = null
      this.isLoadingHeatmap = true

      try {
        const response = await api.get(
          `gps/gsmcoverage/?lat=${heatmapCenter.lat}&lng=${heatmapCenter.lng}`
        )

        this.handleHeatmapDataLoaded(heatmapCenter, response.data)
      } catch (error) {
        this.handleHeatmapDataLoadError(heatmapCenter, error)
      }
    },

    handleHeatmapDataLoaded(heatmapDataMapCenter, heatmapData) {
      if (!this.displayHeatmap) {
        return
      }

      if (!this.heatmapCenter) {
        return
      }

      if (
        this.heatmapCenter.lat !== heatmapDataMapCenter.lat ||
        this.heatmapCenter.lng !== heatmapDataMapCenter.lng
      ) {
        return
      }

      this.isLoadingHeatmap = false
      this.heatmapLoadError = null

      this.heatmapData = heatmapData.map(weightedPosition => {
        return {
          location: new window.google.maps.LatLng(
            weightedPosition.location.lat,
            weightedPosition.location.lng
          ),
          weight: weightedPosition.strength || 0
        }
      })
    },

    handleHeatmapDataLoadError(heatmapDataMapCenter, error) {
      if (!this.displayHeatmap) {
        return
      }

      if (!this.heatmapCenter) {
        return
      }

      if (
        this.heatmapCenter.lat !== heatmapDataMapCenter.lat ||
        this.heatmapCenter.lng !== heatmapDataMapCenter.lng
      ) {
        return
      }

      this.isLoadingHeatmap = false
      this.heatmapData = null
      this.heatmapLoadError = `GSM Coverage load failed: ${error}`
    },

    selectMapType(mapTypeId) {
      this.selectedMapTypeId = mapTypeId
    }
  },

  computed: {
    dateConfig() {
      return {
        enableTime: false,
        dateFormat: 'Y-m-d',
        enable: this.historyDates,
        minDate: dayjs()
          .subtract(3, 'month')
          .subtract(1, 'day')
          .toDate(),
        maxDate: new Date(),
        locale: {
          firstDayOfWeek: 1
        }
      }
    },

    zoomButtonsColorClass() {
      let color = 'is-dark'

      if (this.selectedMapTypeId === 'satellite') {
        color = 'is-white'
      }

      return color
    },

    zoomInMapDisabled() {
      return this.currentZoom >= MAP_MAX_ZOOM
    },

    zoomOutMapDisabled() {
      return this.currentZoom <= MAP_MIN_ZOOM
    },

    sliderVisible() {
      return this.gpsPositions || this.isLoadingGPSPositions
    },

    isLoadingHistoryDates() {
      const isLoadingHistoryDates = `${this.gpsStorePath}/${IS_LOADING_HISTORY_DATES}`
      return this.$store.getters[isLoadingHistoryDates]
    },

    historyDates() {
      const getHistoryDates = `${this.gpsStorePath}/${GET_HISTORY_DATES}`
      return this.$store.getters[getHistoryDates]
    },

    historyDatesLoadError() {
      const getHistoryDatesLoadError = `${this.gpsStorePath}/${GET_HISTORY_DATES_LOAD_ERROR}`
      return this.$store.getters[getHistoryDatesLoadError]
    },

    hasNoHistoryDates() {
      const hasEmptyHistoryDatesLoadResult = `${this.gpsStorePath}/${HAS_EMPTY_HISTORY_DATES_LOAD_RESULT}`
      return this.$store.getters[hasEmptyHistoryDatesLoadResult]
    },

    isLoadingGPSPositions() {
      const isLoadingGPSPositions = `${this.gpsStorePath}/${IS_LOADING_GPS_POSITIONS}`
      return this.$store.getters[isLoadingGPSPositions]
    },

    gpsPositions() {
      const getGPSPositions = `${this.gpsStorePath}/${GET_GPS_POSITIONS}`
      return this.$store.getters[getGPSPositions]
    },

    gpsPositionsLoadError() {
      const getGPSPositionsLoadError = `${this.gpsStorePath}/${GET_GPS_POSITIONS_LOAD_ERROR}`
      return this.$store.getters[getGPSPositionsLoadError]
    },

    hasNoGPSPositions() {
      const hasEmptyGPSPositionsLoadResult = `${this.gpsStorePath}/${HAS_EMPTY_GPS_POSITIONS_LOAD_RESULT}`
      return this.$store.getters[hasEmptyGPSPositionsLoadResult]
    },

    currentPosition() {
      if (!this.gpsPositions || this.gpsPositions.length <= this.elapsed) {
        return null
      }

      return this.gpsPositions[this.elapsed]
    },

    currentTimeStamp() {
      if (!this.currentPosition) {
        return null
      }

      return dayjs(this.currentPosition.timestamp).format('HH:mm:ss')
    },

    maxTimeStamp() {
      if (!this.gpsPositions || this.gpsPositions.length === 0) {
        return null
      }

      return dayjs(this.gpsPositions[this.gpsPositions.length - 1].timestamp).format('HH:mm:ss')
    },

    currentPositionCoverage() {
      return this.calculateCoverage(this.currentPosition)
    },

    currentPositionCsq() {
      if (!this.currentPosition) {
        return null
      }

      return this.currentPosition.csq
    },

    currentPositionGps() {
      return this.calculateGPS(this.currentPosition)
    },

    currentPositionBattery() {
      if (!this.currentPosition) {
        return null
      }

      return this.currentPosition.battery
    },

    currentPositionLatency() {
      return this.calculateLatency(this.currentPosition)
    },

    currentPositionLatitude() {
      if (!this.currentPosition) {
        return null
      }

      if (!this.currentPosition.location.lat) {
        return null
      }

      const roundedLat = Math.round(this.currentPosition.location.lat * 10000) / 10000
      return roundedLat
    },

    currentPositionLongitude() {
      if (!this.currentPosition) {
        return null
      }

      if (!this.currentPosition.location.lng) {
        return null
      }

      const roundedLng = Math.round(this.currentPosition.location.lng * 10000) / 10000
      return roundedLng
    }
  },

  watch: {
    historyDates(dates) {
      this.updateSelectedDate(dates)
    },

    selectedDate(date) {
      const loadGPSPositionsForDateAction = `${this.gpsStorePath}/${LOAD_GPS_POSITIONS_FOR_DATE}`
      this.$store.dispatch(loadGPSPositionsForDateAction, { date })
    },

    gpsPositions(positions) {
      this.updateSegments(positions)
    },

    currentPosition(position) {
      this.updateGPSMarker(position)
    },

    displayHeatmap() {
      if (this.displayHeatmap) {
        this.setHeatmapUpdateNeeded()
      } else {
        this.heatmapCenter = null
        this.heatmapData = null
        this.isLoadingHeatmap = false
        this.heatmapLoadError = null
      }
    },

    heatmapData(data) {
      this.heatmap.setData(data || [])
    },

    selectedMapTypeId(mapTypeId) {
      this.map.setMapTypeId(mapTypeId)
    },

    elapsed(newElapsed, oldElapsed) {
      if (Math.abs(newElapsed - oldElapsed) <= 1) {
        return
      }

      if (this.currentPositionCoverage > 0) {
        // No need to step GPS marker to all locations when the current position
        // will be used.
        return
      }

      // Make sure to move the gps marker to all position between oldElapsed
      // and newElapsed, otherwise the GPS marker may stop in the wrong position
      // when dragging into a segment with coverage == 0
      if (newElapsed > oldElapsed) {
        for (let i = oldElapsed + 1; i <= newElapsed; i++) {
          const position = this.gpsPositions[i]
          this.updateGPSMarker(position)
        }
      } else {
        for (let i = oldElapsed - 1; i >= newElapsed; i--) {
          const position = this.gpsPositions[i]
          this.updateGPSMarker(position)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.main {
  height: 100%;
  width: 100%;
  position: relative;
}

.map {
  height: 100%;
  width: 100%;
}

.zoom-buttons {
  position: absolute;
  top: 50%;
  right: 1.5rem;
  transform: translateY(-50%);
  z-index: 1;

  display: flex;
  flex-direction: column;

  .button {
    margin: 0 0 0.75rem 0 !important;

    &:last-of-type {
      margin: 0 !important;
    }
  }
}

.panel {
  margin: 0 !important;
  background: white;
  max-height: calc(100% - 3rem);
  font-size: 0.85rem !important;
  width: auto;
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  border-radius: 8px;
  z-index: 25;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 1024px) {
    width: 20rem;
  }

  .panel-content {
    height: 100%;
    flex-grow: 1;
    padding: 0.75rem;
  }

  .title {
    margin: 0;
    padding-bottom: 0.75rem;
  }
}

.gps-controls {
  padding: 0.75rem 1.25rem;
  position: absolute;
  right: 1.5rem;
  bottom: 1.5rem;
  left: 1.5rem;
  margin-bottom: 0 !important;
  min-height: 36px;
}

.drag-elapsed {
  @media screen and (min-width: 1024px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &-timer {
    margin-left: 0.75rem;
    white-space: nowrap;
    display: none;

    @media screen and (min-width: 1024px) {
      display: block;
    }
  }
}

.info-panel-container {
  position: absolute;
  top: 5rem;
  bottom: 5rem;
}

.panel.info-panel {
  overflow: visible;
  top: auto;
  bottom: 0rem;
  width: 10rem;

  .panel-content {
    overflow: auto;
    padding: 0.75rem;
  }
}

.heatmap {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  left: auto;
  width: auto;
}

.field {
  min-width: 15px;
}

.field-label label {
  white-space: nowrap;
}
</style>
